import {SERVER_CONTEXT_PATH} from '../../const';

import {EnumAccessibility} from '../../api';

/**
 * Klientská prezentační vrstva číselníku přístupnosti. Kódy (`kod`) jsou generované z api.yml
 * (`EnumAccessibility`) → jediný zdroj pravdy sdílený se serverem; klient k nim doplňuje pouze
 * ikonu a `nazev` (delší popisek do tooltipu). Kanonické popisky jsou i v api.yml (x-enum-descriptions).
 */
export interface AccessibilityItem {
    kod: EnumAccessibility;
    nazev: string;
    icon: string;
}

const accessibilityIconUrl = (kod: EnumAccessibility): string =>
    `${SERVER_CONTEXT_PATH}/api/files/img/accessibility/${kod}.svg`;

export const ACCESSIBILITY_ITEMS: AccessibilityItem[] = [
    {kod: EnumAccessibility.VSTUP_HLAVNI, nazev: 'Bezbariérový vstup hlavním vchodem'},
    {kod: EnumAccessibility.VSTUP_BOCNI, nazev: 'Bezbariérový vstup bočním vchodem'},
    {kod: EnumAccessibility.LISINY_RAMPA, nazev: 'Ližiny nebo rampa'},
    {kod: EnumAccessibility.OBTIZNY_POVRCH, nazev: 'Obtížný povrch'},
    {kod: EnumAccessibility.OBTIZNY_SKLON, nazev: 'Obtížný sklon'},
    {kod: EnumAccessibility.PLOSINA_VYTAH, nazev: 'Plošina / výtah pro osoby s omez. schop. pohybu'},
    {kod: EnumAccessibility.SCHODY, nazev: 'Schody'},
    {kod: EnumAccessibility.TOCITE_SCHODISTE, nazev: 'Točité schodiště'},
    {kod: EnumAccessibility.VYTAH, nazev: 'Výtah'},
    {kod: EnumAccessibility.WC_BEZNE, nazev: 'WC běžné'},
    {kod: EnumAccessibility.WC1, nazev: 'WC I.'},
    {kod: EnumAccessibility.WC2, nazev: 'WC II.'},
    {kod: EnumAccessibility.UZKE_DVERE, nazev: 'Úzké dveře nebo průjezdy'},
].map(item => ({...item, icon: accessibilityIconUrl(item.kod)}));

export const ACCESSIBILITY_MAP = new Map<string, AccessibilityItem>(
    ACCESSIBILITY_ITEMS.map(item => [item.kod, item]),
);

/** Intent karty (ui-v4) podle závažnosti přístupnosti objektu. */
export type AccessibilitySeverityIntent = 'success' | 'warning' | 'danger';

/**
 * Kódy přístupnosti určující barevné rozlišení objektu, seřazené od nejzávažnějšího.
 * Objekt může mít více značek – vybíráme vždy tu nejzávažnější (červená > oranžová > zelená).
 */
const ACCESSIBILITY_SEVERITY: {kod: EnumAccessibility; intent: AccessibilitySeverityIntent}[] = [
    {kod: EnumAccessibility.OBJEKT_NEPRISTUPNY, intent: 'danger'}, // Objekt obtížně přístupný nebo nepřístupný → červená
    {kod: EnumAccessibility.OBJEKT_CASTECNE, intent: 'warning'}, // Objekt částečně přístupný → oranžová
    {kod: EnumAccessibility.OBJEKT_PRISTUPNY, intent: 'success'}, // Objekt přístupný → zelená
];

/**
 * Vrátí intent nejzávažnější značky přístupnosti obsažené v `codes`, nebo `undefined`,
 * pokud objekt žádnou z rozlišovacích značek nemá (pak se barevný sideHeader nezobrazuje).
 */
export const getAccessibilitySeverityIntent = (codes: string[]): AccessibilitySeverityIntent | undefined => {
    for (const {kod, intent} of ACCESSIBILITY_SEVERITY) {
        if (codes.includes(kod)) return intent;
    }
    return undefined;
};

export const parseAccessibilityValue = (value: string | null | undefined): string[] => {
    if (!value) return [];
    return value.split(';').filter(Boolean);
};

/** Všechny platné kódy přístupnosti (včetně rozlišovacích OBJEKT_*, které nemají vlastní ikonu). */
const ALL_ACCESSIBILITY_CODES = new Set<string>(Object.values(EnumAccessibility));

/**
 * Kódy, které samy o sobě nemohou být běžným textem (obsahují podtržítko, nebo jde o WC I./II.).
 * Jednoslovné kódy jako `schody` nebo `vytah` jsou zároveň běžná česká slova, takže je za značku
 * přístupnosti považujeme jen ve společnosti dalšího kódu – viz {@link looksLikeAccessibilityValue}.
 */
const isStrongAccessibilityCode = (code: string): boolean => code.includes('_') || /^wc\d$/.test(code);

/**
 * Heuristika pro místa, kde je k dispozici jen surová hodnota atributu bez jeho typu
 * (např. log změn atributů – LogAtributVO nenese `typ`). Za hodnotu přístupnosti považujeme
 * dvojici hodnot „před/po“ tehdy, když všechny neprázdné hodnoty tvoří výhradně platné kódy
 * a alespoň jedna z nich je jednoznačná (více kódů nebo kód, který nemůže být běžným textem).
 */
export const looksLikeAccessibilityValue = (...values: (string | null | undefined)[]): boolean => {
    const codeLists = values.map(parseAccessibilityValue).filter(codes => codes.length > 0);
    if (codeLists.length === 0) return false;
    if (!codeLists.every(codes => codes.every(code => ALL_ACCESSIBILITY_CODES.has(code)))) return false;
    return codeLists.some(codes => codes.length > 1 || codes.some(isStrongAccessibilityCode));
};

export const serializeAccessibilityValue = (codes: string[]): string | null => {
    const filtered = codes.filter(Boolean);
    return filtered.length > 0 ? filtered.join(';') : null;
};

export const renderAccessibilityIcons = (html?: string | null): string => {
    if (!html || !html.includes('accessibility-value')) {
        return html ?? '';
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('.accessibility-value').forEach((el: Element) => {
        const activeCodes = parseAccessibilityValue(el.getAttribute('data-codes'));
        // Vykreslíme VŠECHNY ikony přístupnosti; ty, které objekt nemá, zobrazíme zašedle (neaktivní),
        // aby byl na první pohled patrný i výčet vlastností, které objekt nesplňuje.
        el.innerHTML = ACCESSIBILITY_ITEMS.map(item => {
            const inactive = !activeCodes.includes(item.kod);
            const cls = `accessibility-display__icon${inactive ? ' accessibility-display__icon--inactive' : ''}`;
            return `<img src="${item.icon}" alt="${item.nazev}" title="${item.nazev}" class="${cls}" />`;
        }).join('');
        el.classList.add('accessibility-display');
    });
    return doc.body.innerHTML;
};
